import axios from 'axios';
import 'react';
import '@bugsnag/js';
import '@bugsnag/plugin-react';
import { ApiError, ApiValidationError, ApiNotFoundError, ApiInvalidRequestError, ApiAvailabilityError, ApiActionFailedError, ApiAuthError, ApplicationError } from '../errors/index.js';
const handleSuccessResponse = _ref => {
  let {
    data
  } = _ref;
  return data;
};
const handleApiError = _ref2 => {
  let {
    response: {
      data: {
        type,
        message,
        data
      }
    }
  } = _ref2;
  switch (type) {
    case 'auth':
      return new ApiAuthError(message);
    case 'actionFailed':
      return new ApiActionFailedError(message, data);
    case 'availability':
      return new ApiAvailabilityError(message, data);
    case 'invalidRequest':
      return new ApiInvalidRequestError(message);
    case 'notFound':
      return new ApiNotFoundError(message);
    case 'validation':
      return new ApiValidationError(message, data);
    default:
      return new ApiError(message);
  }
};
const handleErrorResponse = error => {
  if (error.response) {
    return Promise.reject(handleApiError(error));
  }
  return Promise.reject(error);
};
const createApiService = () => {
  let api = null;
  let token = null;
  const setToken = newToken => {
    token = newToken;
  };
  const configure = _ref => {
    let {
      baseUrl: baseURL
    } = _ref;
    if (!baseURL) {
      throw new ApplicationError('Cannot create axios instance without baseUrl');
    }
    api = axios.create({
      baseURL
    });
    api.interceptors.request.use(config => {
      const customConfig = config;
      customConfig.headers.Authorization = `Bearer ${token}`;
      return customConfig;
    }, error => Promise.reject(error));
    api.interceptors.response.use(handleSuccessResponse, handleErrorResponse);
  };
  const getApi = () => api;
  return {
    configure,
    setToken,
    getApi
  };
};
var apiService = createApiService();

/* eslint-disable import/prefer-default-export */
const getAgeGroups = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/age-groups', {
    params
  });
};
const postBankHoliday = bankHoliday => apiService.getApi().post('/bank-holidays', bankHoliday);
const getBankHolidays = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/bank-holidays', {
    params
  });
};
const updateBankHoliday = (id, bankHoliday) => apiService.getApi().put(`/bank-holidays/${id}`, bankHoliday);
const deleteBankHoliday = id => apiService.getApi().delete(`/bank-holidays/${id}`);
const postCleaning = cleaning => apiService.getApi().post('/cleanings', cleaning);
const getCleanings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/cleanings', {
    params
  });
};
const updateCleaning = (id, cleaning) => apiService.getApi().put(`/cleanings/${id}`, cleaning);
const deleteCleaning = id => apiService.getApi().delete(`/cleanings/${id}`);
const postFee = fee => apiService.getApi().post('/fees', fee);
const getFees = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/fees', {
    params
  });
};
const updateFee = (id, fee) => apiService.getApi().put(`/fees/${id}`, fee);
const deleteFee = id => apiService.getApi().delete(`/fees/${id}`);
const getBookings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/bookings', {
    params
  });
};
const postBookings = bookings => apiService.getApi().post('/bookings/import', bookings);
const postBooking = booking => apiService.getApi().post('/bookings', booking);
const getBooking = id => apiService.getApi().get(`/bookings/${id}`);
const updateBooking = (id, data) => apiService.getApi().patch(`/bookings/${id}`, data);
const confirmBooking = id => apiService.getApi().patch(`/bookings/${id}/confirm`);
const cancelBooking = (id, cancellationReason) => apiService.getApi().patch(`/bookings/${id}/cancel`, cancellationReason);
const cancelBookings = ids => apiService.getApi().patch('/bookings/cancel', ids, {
  headers: {
    bulk: true
  }
});
const restoreBooking = id => apiService.getApi().patch(`/bookings/${id}/restore`);
const deleteBooking = id => apiService.getApi().delete(`/bookings/${id}`);
const createInvoiceForBooking = id => apiService.getApi().post(`/bookings/${id}/invoice`);
const deleteInvoiceForBooking = id => apiService.getApi().delete(`/bookings/${id}/invoice`);
const postSpaceBooking = (bookingId, data) => apiService.getApi().post(`/bookings/${bookingId}/space-bookings`, data);
const updateSpaceBooking = (bookingId, id, data) => apiService.getApi().patch(`/bookings/${bookingId}/space-bookings/${id}`, data);
const cancelSpaceBooking = (bookingId, id) => apiService.getApi().patch(`/bookings/${bookingId}/space-bookings/${id}/cancel`);
const getBookingFees = bookingId => apiService.getApi().get(`/bookings/${bookingId}/fees`);
const postBookingFee = (bookingId, fee) => apiService.getApi().post(`/bookings/${bookingId}/fees`, fee);
const updateBookingFee = (bookingId, id, data) => apiService.getApi().patch(`/bookings/${bookingId}/fees/${id}`, data);
const deleteBookingFee = (bookingId, id) => apiService.getApi().delete(`/bookings/${bookingId}/fees/${id}`);
const getBookingActions = bookingId => apiService.getApi().get(`/bookings/${bookingId}/actions`);

// eslint-disable-next-line import/prefer-default-export
const getBookingStates = () => apiService.getApi().get('/booking-states');
const getBookingsCalendarForSpace = function (spaceId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/calendars/bookings/spaces/${spaceId}`, {
    params
  });
};
const getRecurringBookingsCalendarForSpace = function (spaceId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/calendars/recurring-bookings/spaces/${spaceId}`, {
    params
  });
};

// eslint-disable-next-line import/prefer-default-export
const getClusters = () => apiService.getApi().get(`/clusters`);
const postCustomer = customer => apiService.getApi().post('/customers', customer);
const getCustomers = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/customers`, {
    params
  });
};
const getCustomer = customerId => apiService.getApi().get(`/customers/${customerId}`);
const updateCustomer = (id, data) => apiService.getApi().patch(`/customers/${id}`, data);
const deleteCustomer = id => apiService.getApi().delete(`/customers/${id}`);
const blockCustomer = id => apiService.getApi().put(`/block/${id}`);
const changePasswordCustomer = (id, data) => apiService.getApi().put(`/change-password/${id}`, data);
const resetPasswordCustomer = id => apiService.getApi().post(`/reset-password/${id}`);

/**
 * Constant : InviteCustomer
 * URL params :
 * @param {string} id user ID
 *
 * Body params :
 * @param {string} email user email
 *
 * @returns empty with 200 status code.
 * */
const inviteCustomer = function inviteCustomer(id, data) {
  return apiService.getApi().post(`/customers/${id}/invite`, data);
};
const getCustomerBookings = function (customerId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/customers/${customerId}/bookings`, {
    params
  });
};
const getCustomerBooking = (customerId, bookingId) => apiService.getApi().get(`/customers/${customerId}/bookings/${bookingId}`);
const postCustomerSpaceBooking = (customerId, bookingId, data) => apiService.getApi().post(`/customers/${customerId}/bookings/${bookingId}/space-bookings`, data);
const updateCustomerSpaceBooking = (customerId, bookingId, spaceBookingId, data) => apiService.getApi().patch(`/customers/${customerId}/bookings/${bookingId}/space-bookings/${spaceBookingId}`, data);
const cancelCustomerSpaceBooking = (customerId, bookingId, spaceBookingId) => apiService.getApi().patch(`/customers/${customerId}/bookings/${bookingId}/space-bookings/${spaceBookingId}/cancel`);
const cancelCustomerBooking = (customerId, bookingId, cancellationReason) => apiService.getApi().patch(`/customers/${customerId}/bookings/${bookingId}/cancel`, cancellationReason);
const getCustomerRecurringBookings = function (customerId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/customers/${customerId}/recurring-bookings`, {
    params
  });
};
const getCustomerRecurringBooking = (customerId, bookingId) => apiService.getApi().get(`/customers/${customerId}/recurring-bookings/${bookingId}`);
const updateCustomerRecurringBooking = (customerId, bookingId, data) => apiService.getApi().patch(`/customers/${customerId}/recurring-bookings/${bookingId}`, data);
const getCustomerRecurringBookingDates = (customerId, bookingId) => apiService.getApi().get(`/customers/${customerId}/recurring-bookings/${bookingId}/dates`);
const getCustomerRecurringBookingAvailableDates = (customerId, bookingId) => apiService.getApi().get(`/customers/${customerId}/recurring-bookings/${bookingId}/available-dates`);
const acceptCustomerRecurringBooking = (customerId, bookingId, cancelledDates) => apiService.getApi().patch(`/customers/${customerId}/recurring-bookings/${bookingId}/accept`, {
  cancelledDates
});
const cancelCustomerRecurringBooking = (customerId, bookingId, cancellationReason) => apiService.getApi().patch(`/customers/${customerId}/recurring-bookings/${bookingId}/cancel`, cancellationReason);
const postCustomerRecurringSpaceBooking = (customerId, bookingId, data) => apiService.getApi().post(`/customers/${customerId}/recurring-bookings/${bookingId}/recurring-space-bookings`, data);
const updateCustomerRecurringSpaceBooking = (customerId, bookingId, spaceBookingId, data) => apiService.getApi().patch(`/customers/${customerId}/recurring-bookings/${bookingId}/recurring-space-bookings/${spaceBookingId}`, data);
const cancelCustomerRecurringSpaceBooking = (customerId, bookingId, spaceBookingId) => apiService.getApi().patch(`/customers/${customerId}/recurring-bookings/${bookingId}/recurring-space-bookings/${spaceBookingId}/cancel`);

/* eslint-disable import/prefer-default-export */
const getInvoiceContacts = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/invoice-contacts', {
    params
  });
};
const postInvoiceContact = data => apiService.getApi().post('/invoice-contacts', data);
const postOperatingTimes = operatingTimes => apiService.getApi().post('/operating-times', operatingTimes);
const getOperatingTimes = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/operating-times', {
    params
  });
};
const updateOperatingTimes = (id, operatingTimes) => apiService.getApi().put(`/operating-times/${id}`, operatingTimes);
const deleteOperatingTimes = id => apiService.getApi().delete(`/operating-times/${id}`);

// eslint-disable-next-line import/prefer-default-export
const sendRebookingEmail = (type, bookingIds) => apiService.getApi().post(`/recurring-bookings/rebooking/email`, {
  type,
  bookingIds
});
const getRecurringBookings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/recurring-bookings', {
    params
  });
};
const getRecurringBooking = id => apiService.getApi().get(`/recurring-bookings/${id}`);
const verifyRefCode = refCode => apiService.getApi().get(`/booking-requests/verify/${refCode}`);
const postRecurringBooking = booking => apiService.getApi().post(`/recurring-bookings`, booking);
const updateRecurringBooking = (id, data) => apiService.getApi().patch(`/recurring-bookings/${id}`, data);
const confirmRecurringBooking = (id, recurringBookingInvoiceData) => apiService.getApi().patch(`/recurring-bookings/${id}/confirm`, recurringBookingInvoiceData);
const rebookRecurringBooking = id => apiService.getApi().post(`/recurring-bookings/${id}/rebook`);
const cancelRecurringBooking = (id, cancellationReason) => apiService.getApi().patch(`/recurring-bookings/${id}/cancel`, cancellationReason);
const deleteRecurringBooking = id => apiService.getApi().delete(`/recurring-bookings/${id}`);
const postRecurringSpaceBooking = (recurringBookingId, data) => apiService.getApi().post(`/recurring-bookings/${recurringBookingId}/recurring-space-bookings`, data);
const updateRecurringSpaceBooking = (recurringBookingId, id, data) => apiService.getApi().patch(`/recurring-bookings/${recurringBookingId}/recurring-space-bookings/${id}`, data);
const cancelRecurringSpaceBooking = (recurringBookingId, id, data) => apiService.getApi().patch(`/recurring-bookings/${recurringBookingId}/recurring-space-bookings/${id}/cancel`, data);
const getRecurringBookingActions = recurringBookingId => apiService.getApi().get(`/recurring-bookings/${recurringBookingId}/actions`);
const createInvoiceForRecurringBooking = recurringBookingId => apiService.getApi().post('/recurring-bookings/'.concat(recurringBookingId, '/invoice'));
const deleteInvoiceForRecurringBooking = recurringBookingId => apiService.getApi().delete('/recurring-bookings/'.concat(recurringBookingId, '/invoice'));
const getBookingsReport = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/bookings', {
    params
  });
};
const getPlannersReport = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/planners', {
    params
  });
};
const getSchoolPlannersReport = function (schoolId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/reports/planners/schools/${schoolId}`, {
    params
  });
};
const sendSchoolPlannersReport = function (schoolId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().post(`/reports/planners/schools/${schoolId}`, null, {
    params
  });
};
const getCancellationReport = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/cancellations', {
    params
  });
};
const getBookingClashesReport = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/clashes/bookings', {
    params
  });
};
const getRecurringBookingClashesReport = () => apiService.getApi().get('/reports/clashes/recurring-bookings');
const getCustomerReport = () => apiService.getApi().get('/reports/customers');
const getSchoolSchedule = function (schoolId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/reports/schedules/schools/${schoolId}`, {
    params,
    responseType: 'arraybuffer',
    headers: {
      Accept: 'application/pdf'
    }
  });
};
const getPromotionPlatformReport = () => apiService.getApi().get('/reports/promotion-platform');
const getNewCustomersReport = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/new-customers', {
    params
  });
};
const getCustomersWithNoFurtherBookings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/no-further-bookings', {
    params
  });
};
const getCustomersWithPaymentMethod = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/reports/payment-method-added', {
    params
  });
};
const getSchool = id => apiService.getApi().get(`/schools/${id}`);
const getSchools = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/schools`, {
    params
  });
};
const postSchool = school => apiService.getApi().post('/schools', school);
const postSchools = schools => apiService.getApi().post('/schools', schools, {
  headers: {
    bulk: true
  }
});
const getSchoolTermDates = id => apiService.getApi().get(`/schools/${id}/term-dates`);
const getSchoolSites = id => apiService.getApi().get(`/schools/${id}/sites`);
const getSchoolCalendar = function (schoolId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/schools/${schoolId}/calendar`, {
    params
  });
};
const getSchoolLocations = () => apiService.getApi().get(`/school-locations`);
const updateSchool = (id, data) => apiService.getApi().put(`/schools/${id}`, data);
const patchSchool = (id, data) => apiService.getApi().patch(`/schools/${id}`, data);
const deleteSchool = id => apiService.getApi().delete(`/schools/${id}`);
const getPartners = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/school-partners`, {
    params
  });
};
const createPartner = partner => apiService.getApi().post(`/school-partners`, partner);
const removePartner = id => apiService.getApi().delete(`/school-partners/${id}`);
const getFaqs = () => apiService.getApi().get(`/faqs`);
const postFaq = faq => apiService.getApi().post('/faqs', faq);
const updateFaq = (id, data) => apiService.getApi().put(`/faqs/${id}`, data);
const deleteFaq = id => apiService.getApi().delete(`/faqs/${id}`);
const getNews = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/blogs`, {
    params
  });
};
const postNews = news => apiService.getApi().post('/blogs', news);
const updateNews = (id, data) => apiService.getApi().put(`/blogs/${id}`, data);
const getJobs = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/jobs`, {
    params
  });
};
const getJob = id => apiService.getApi().get(`/jobs/${id}`);
const postJob = faq => apiService.getApi().post('/jobs', faq);
const updateJob = (id, data) => apiService.getApi().put(`/jobs/${id}`, data);
const removeJob = id => apiService.getApi().delete(`/jobs/${id}`);
const getContent = type => apiService.getApi().get(`/content/${type}`);
const updateContent = data => apiService.getApi().put(`/content`, data);
const postSchoolBooking = schoolBooking => apiService.getApi().post('/school-bookings', schoolBooking);
const postSchoolBookings = schoolBookings => apiService.getApi().post('/school-bookings', schoolBookings, {
  headers: {
    bulk: true
  }
});
const getSchoolBookings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/school-bookings', {
    params
  });
};
const updateSchoolBooking = (id, schoolBooking) => apiService.getApi().put(`/school-bookings/${id}`, schoolBooking);
const deleteSchoolBooking = id => apiService.getApi().delete(`/school-bookings/${id}`);
const postSchoolUser = schoolUser => apiService.getApi().post('/school-users', schoolUser);
const getSchoolUsers = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/school-users', {
    params
  });
};
const updateSchoolUser = (id, schoolUser) => apiService.getApi().put(`/school-users/${id}`, schoolUser);
const inviteSchoolUser = id => apiService.getApi().post(`/school-users/${id}/invite`);
const deleteSchoolUser = id => apiService.getApi().delete(`/school-users/${id}`);
const postSite = site => apiService.getApi().post('/sites', site);
const getSite = id => apiService.getApi().get(`/sites/${id}`);
const updateSite = (id, site) => apiService.getApi().put(`/sites/${id}`, site);
const getSlots = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/slots', {
    params
  });
};
const getRecurringSlots = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/recurring-slots', {
    params
  });
};

// eslint-disable-next-line import/prefer-default-export
const getSlotRules = () => apiService.getApi().get('/slot-rules');
const postSpace = space => apiService.getApi().post('/spaces', space);
const postSpaces = spaces => apiService.getApi().post('/spaces', spaces, {
  headers: {
    bulk: true
  }
});
const getSpaces = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get(`/spaces`, {
    params
  });
};
const getSpace = id => apiService.getApi().get(`/spaces/${id}`);
const getSpaceTypes = () => apiService.getApi().get('/space-types');
const updateSpace = (id, data) => apiService.getApi().put(`/spaces/${id}`, data);
const deleteSpace = id => apiService.getApi().delete(`/spaces/${id}`);
const getSpaceRates = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/space-rates', {
    params
  });
};
const increaseSpaceRates = data => apiService.getApi().post('/space-rates/increase', data);
const postSpaceRates = data => apiService.getApi().post('/space-rates', data);
const postTermDates = termDates => apiService.getApi().post('/term-dates', termDates);
const updateTermDates = (id, termDates) => apiService.getApi().put(`/term-dates/${id}`, termDates);
const deleteTermDates = id => apiService.getApi().delete(`/term-dates/${id}`);
const postViewing = viewing => apiService.getApi().post('/viewings', viewing);
const getViewings = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/viewings', {
    params
  });
};
const updateViewing = (id, viewing) => apiService.getApi().put(`/viewings/${id}`, viewing);
const deleteViewing = id => apiService.getApi().delete(`/viewings/${id}`);
const postYear = () => apiService.getApi().post('/years');
const getYears = function () {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return apiService.getApi().get('/years', {
    params
  });
};
const getYear = id => apiService.getApi().get(`/years/${id}`);
const updateYear = (id, update) => apiService.getApi().patch(`/years/${id}`, update);
const addSupportingDocuments = function addSupportingDocuments(id, data) {
  return apiService.getApi().post(`/customers/${id}/supporting-documents`, data);
};
const getSupportingDocuments = function (customerId) {
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return apiService.getApi().get(`/customers/${customerId}/supporting-documents`, {
    params
  });
};
const updateSupportingDocuments = (id, documentId, data) => apiService.getApi().patch(`/customers/${id}/supporting-documents/${documentId}`, data);
const updateAllSupportingDocuments = function updateAllSupportingDocuments(id, data) {
  return apiService.getApi().put(`/customers/${id}/supporting-documents`, data);
};
const deleteSupportingDocuments = function deleteSupportingDocuments(id, documentId) {
  return apiService.getApi().delete(`/customers/${id}/supporting-documents/${documentId}`);
};
const getDepositSession = id => apiService.getApi().post(`customers/${id}/customerDeposits/checkout-session`);
const confirmPaymentSessionId = function confirmPaymentSessionId(customerId, sessionId) {
  return apiService.getApi().get(`/customers/${customerId}/customerDeposits/checkout-session/${sessionId}`);
};
const addDeposit = function addDeposit(id, data) {
  return apiService.getApi().post(`/customers/${id}/customerDeposits`, data);
};
const deleteDeposit = function deleteDeposit(id, depositId) {
  return apiService.getApi().delete(`/customers/${id}/customerDeposits/${depositId}`);
};
const getSetupIntent = id => apiService.getApi().post(`customers/${id}/customer-payments/setup-intent`);
const getPaymentMethods = id => apiService.getApi().get(`customers/${id}/customer-payments/payment-methods`);
export default apiService;
export { acceptCustomerRecurringBooking, addDeposit, addSupportingDocuments, blockCustomer, cancelBooking, cancelBookings, cancelCustomerBooking, cancelCustomerRecurringBooking, cancelCustomerRecurringSpaceBooking, cancelCustomerSpaceBooking, cancelRecurringBooking, cancelRecurringSpaceBooking, cancelSpaceBooking, changePasswordCustomer, confirmBooking, confirmPaymentSessionId, confirmRecurringBooking, createInvoiceForBooking, createInvoiceForRecurringBooking, createPartner, deleteBankHoliday, deleteBooking, deleteBookingFee, deleteCleaning, deleteCustomer, deleteDeposit, deleteFaq, deleteFee, deleteInvoiceForBooking, deleteInvoiceForRecurringBooking, deleteOperatingTimes, deleteRecurringBooking, deleteSchool, deleteSchoolBooking, deleteSchoolUser, deleteSpace, deleteSupportingDocuments, deleteTermDates, deleteViewing, getAgeGroups, getBankHolidays, getBooking, getBookingActions, getBookingClashesReport, getBookingFees, getBookingStates, getBookings, getBookingsCalendarForSpace, getBookingsReport, getCancellationReport, getCleanings, getClusters, getContent, getCustomer, getCustomerBooking, getCustomerBookings, getCustomerRecurringBooking, getCustomerRecurringBookingAvailableDates, getCustomerRecurringBookingDates, getCustomerRecurringBookings, getCustomerReport, getCustomers, getCustomersWithNoFurtherBookings, getCustomersWithPaymentMethod, getDepositSession, getFaqs, getFees, getInvoiceContacts, getJob, getJobs, getNewCustomersReport, getNews, getOperatingTimes, getPartners, getPaymentMethods, getPlannersReport, getPromotionPlatformReport, getRecurringBooking, getRecurringBookingActions, getRecurringBookingClashesReport, getRecurringBookings, getRecurringBookingsCalendarForSpace, getRecurringSlots, getSchool, getSchoolBookings, getSchoolCalendar, getSchoolLocations, getSchoolPlannersReport, getSchoolSchedule, getSchoolSites, getSchoolTermDates, getSchoolUsers, getSchools, getSetupIntent, getSite, getSlotRules, getSlots, getSpace, getSpaceRates, getSpaceTypes, getSpaces, getSupportingDocuments, getViewings, getYear, getYears, increaseSpaceRates, inviteCustomer, inviteSchoolUser, patchSchool, postBankHoliday, postBooking, postBookingFee, postBookings, postCleaning, postCustomer, postCustomerRecurringSpaceBooking, postCustomerSpaceBooking, postFaq, postFee, postInvoiceContact, postJob, postNews, postOperatingTimes, postRecurringBooking, postRecurringSpaceBooking, postSchool, postSchoolBooking, postSchoolBookings, postSchoolUser, postSchools, postSite, postSpace, postSpaceBooking, postSpaceRates, postSpaces, postTermDates, postViewing, postYear, rebookRecurringBooking, removeJob, removePartner, resetPasswordCustomer, restoreBooking, sendRebookingEmail, sendSchoolPlannersReport, updateAllSupportingDocuments, updateBankHoliday, updateBooking, updateBookingFee, updateCleaning, updateContent, updateCustomer, updateCustomerRecurringBooking, updateCustomerRecurringSpaceBooking, updateCustomerSpaceBooking, updateFaq, updateFee, updateJob, updateNews, updateOperatingTimes, updateRecurringBooking, updateRecurringSpaceBooking, updateSchool, updateSchoolBooking, updateSchoolUser, updateSite, updateSpace, updateSpaceBooking, updateSupportingDocuments, updateTermDates, updateViewing, updateYear, verifyRefCode };